
import {
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  ref,
  watch
} from "vue";
import { useStore } from "vuex";
import {
  ContentComment,
  Course,
  CourseTrack,
  CourseTrackMandatory
} from "../../../../domain/entities/entities";
import { wrapLoading } from "../../utils/wrap_loading";
import { dispatchError } from "../../utils/error_handling";
import { Carousel, Navigation, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import {
  disableBodyOverflow,
  enableBodyOverflow,
  toLocaleDateTime
} from "../mixins";
import CircleProgress from "./CircleProgress.vue";
import { useRoute, useRouter } from "vue-router";
import { computed } from "@vue/reactivity";
import CommentsModal from "./DashboardComments/CommentsModal.vue";
import Comment from "./DashboardComments/Comment.vue";
import { getOrderFromURL } from "../../utils/order_utils";
import ModalIntroductoryContent from "./ModalIntroductoryContent.vue";
import IntroductoryContentModal from "./ModalIntroductoryContent.vue";
import course from "../../components/application/Course.vue";
import { Content } from "../../views/content/helpers";
import ContentFallback from "./moldure/ContentFallback.vue";
import { updateQueries } from "../../utils/routing_utils";
import CourseTracksSection from "./mandatoryCourseTrack/CourseTracksSection.vue";
import CourseTrackItem from "./mandatoryCourseTrack/CourseTrackItem.vue";
import { Order } from "../../../../domain/entities/order";
import Search from "./inputs/Search.vue";
import { updateQuery } from "../../../repository/utils/routing_utils";
import FilterSelect from "./inputs/FilterSelect.vue";

export default defineComponent({
  name: "MandatoryCourseTrack",
  components: {
    FilterSelect,
    Search,
    CourseTrackItem,
    CourseTracksSection,
    ContentFallback,
    IntroductoryContentModal,
    course,
    ModalIntroductoryContent,
    Comment,
    CommentsModal,
    CircleProgress,
    Carousel,
    Navigation,
    Slide
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const recommendedSettings = {
      itemsToShow: 4,
      itemsToScroll: 4,
      snapAlign: "start",
      transition: 800,
      wrapAround: false
    };

    const recommendedBreakpoints = {
      250: { itemsToShow: 1, itemsToScroll: 1 },
      500: { itemsToShow: 2, itemsToScroll: 2 },
      800: { itemsToShow: 2, itemsToScroll: 2 },
      1000: { itemsToShow: 3, itemsToScroll: 3 },
      1170: { itemsToShow: 4, itemsToScroll: 4 }
    };

    const isCommentsModalOpen: Ref<boolean> = ref(false);

    const commentModalTitle = ref("");

    const openModalIntroductoryContent = ref(false);

    const introductoryContent: Ref<Content | undefined> = ref();

    const activeCourseTrackID: Ref<number | null> = ref(null);

    const selectedFilter = ref(route.query.filterType?.toString() || "0");

    const searchCourseTrack = ref("");

    const comments: ComputedRef<ContentComment[]> = computed(
      () => store.getters["course_module/comments"]
    );

    const mandatoryCourseTrackTrainings: Ref<CourseTrackMandatory> = ref(
      new CourseTrackMandatory()
    );

    const mandatoryCourseTrack: ComputedRef<CourseTrack[]> = computed(
      () => store.getters["course_track_module/requiredCourseTrack"] ?? []
    );

    onBeforeMount(async () => {
      await wrapLoading(async () => {
        await getMandatoryCourseTrack();

        if (mandatoryCourseTrack.value.length > 0) {
          await toggleTrack(mandatoryCourseTrack.value[0].id);
        }
      });
    });

    watch(
      () => activeCourseTrackID.value,
      async () => {
        await cleanMandatoryCourseTrackState();
      }
    );

    watch(
      () => route.query.filterType,
      async () => {
        await getMandatoryCourseTrack();
      }
    );

    const listCourseTrackTraining = async (trackId: number, index: number) => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("course_track_module/listCourseTrackTraining", {
            courseTrackID: trackId,
            index: index
          });
        });
        mandatoryCourseTrackTrainings.value =
          store.getters["course_track_module/courseTrackMandatory"];
      } catch (e) {
        await dispatchError(e);
      }
    };

    const listMandatoryCourseTrackTraining = async (
      courseTrackID: number,
      index: number
    ): Promise<void> => {
      try {
        await store.dispatch("course_track_module/listCourseTrackTraining", {
          courseTrackID: courseTrackID,
          index
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const listMandatoryCourseTrack = async (order: Order): Promise<void> => {
      try {
        await store.dispatch(
          "course_track_module/getRequiredCourseTrack",
          order
        );
      } catch (e) {
        await dispatchError(e);
      }
    };

    const getMandatoryCourseTrack = async (): Promise<void> => {
      try {
        await wrapLoading(async () => {
          const order: Order = getOrderFromURL(route);
          await listMandatoryCourseTrack(order);

          if (!mandatoryCourseTrack.value?.length) return;

          await listMandatoryCourseTrackTraining(
            mandatoryCourseTrack.value[0].id,
            0
          );
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const cleanMandatoryCourseTrackState = async (): Promise<void> => {
      try {
        await store.dispatch(
          "course_track_module/cleanMandatoryCourseTrackState"
        );
      } catch (e) {
        await dispatchError(e);
      }
    };

    const openSection = (courseTrackID: number): void => {
      activeCourseTrackID.value =
        activeCourseTrackID.value === courseTrackID ? null : courseTrackID;
    };

    const toggleTrack = async (courseTrackID: number): Promise<void> => {
      await openSection(courseTrackID);
      if (!activeCourseTrackID.value) return;
      await listCourseTrackTraining(courseTrackID, 0);
    };

    const handleSlide = async (trackId: number) => {
      if (!mandatoryCourseTrackTrainings.value.haveTrainingInTrack) return;
      await listCourseTrackTraining(
        trackId,
        mandatoryCourseTrackTrainings.value.course.length
      );
    };

    const loadContentCommentsById = async (id: number) => {
      const order = getOrderFromURL(route);

      await wrapLoading(async () => {
        await store.dispatch("course_module/loadContentCommentsById", {
          courseID: id,
          order
        });
      });
    };

    const loadCourseCommentsModal = async (course: Course) => {
      if (course.isBlocked) return;

      try {
        commentModalTitle.value = course.title;

        await updateQueries(router, {
          pageDefault: "1",
          courseID: course.id.toString()
        });

        await openCommentsModal();
        await loadContentCommentsById(course.id);
      } catch (e) {
        await dispatchError(e);
      }
    };

    const openCommentsModal = async () => {
      disableBodyOverflow();
      isCommentsModalOpen.value = true;
    };

    const closeCommentsModal = async () => {
      enableBodyOverflow();
      isCommentsModalOpen.value = false;
    };

    const openIntroductoryContent = (course: Course) => {
      if (course.isBlocked) return;
      if (!course.introductoryContent) return;

      introductoryContent.value = course.introductoryContent;
      openModalIntroductoryContent.value = true;
      disableBodyOverflow();
    };

    const closeModalIntroductoryContent = () => {
      openModalIntroductoryContent.value = false;
      enableBodyOverflow();
    };

    const goToCourse = (course: Course) => {
      router.push({ name: "course", params: { course_id: course.id } });
    };

    const sortTrainingTypes = async (): Promise<void> => {
      if (selectedFilter.value !== "") {
        await updateQuery(router, "filterType", selectedFilter.value);
      } else {
        await updateQuery(router, "filterType", "");
      }
    };

    function searchTrack(value: string) {
      searchCourseTrack.value = value;
    }

    return {
      recommendedSettings,
      recommendedBreakpoints,
      comments,
      isCommentsModalOpen,
      commentModalTitle,
      openModalIntroductoryContent,
      introductoryContent,
      mandatoryCourseTrack,
      mandatoryCourseTrackTrainings,
      activeCourseTrackID,
      selectedFilter,
      searchCourseTrack,
      searchTrack,
      sortTrainingTypes,
      toLocaleDateTime,
      closeModalIntroductoryContent,
      openIntroductoryContent,
      openCommentsModal,
      loadCourseCommentsModal,
      closeCommentsModal,
      toggleTrack,
      goToCourse,
      handleSlide
    };
  }
});
